import {
  Button,
  ComponentMultiStyleConfig,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
  useMultiStyleConfig,
  useOutsideClick,
} from "@chakra-ui/react";
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ComponentStyleProps } from "../../../lib/styles/props/component-style-props";
import { hasValue, isNullish } from "../../../lib/util/common-util";
import { CMFormInputSearchAndSelectPopup } from "./_components/CMFormInputSearchAndSelectPopup/CMFormInputSearchAndSelectPopup";
import { SearchDropdownDisplayValue, SearchDropdownOption } from "../../../lib";
import { ChevronDownIcon } from "@chakra-ui/icons";
import log from "loglevel";
import { useCheckEqualityInDiffMode } from "../../../hooks/use-check-equality-in-diff-mode";
import { useDebounce } from "react-use";

export type CMFormInputBaseSearchAndSelectProps<T extends string | number> = {
  editMode?: "editable" | "disabled" | "readOnly";
  variant?: "default" | "diff"; // エラーモードは非対応。
  size?: "sm" | "md" | "lg";

  placeholder?: string;
  placeholderPopover?: string;
  /**
   * 入力可能な最大文字数。
   */
  maxLength?: number;

  /**
   * メニューの最大表示数
   */
  menuShowMaxCount?: number;

  /**
   * 値。
   */
  value?: T | null;

  hideClearButton?: boolean;

  labelSubmit?: string;
  labelClear?: string;

  selectButtonRightIcon?: ReactElement;
  popoverTitle?: string;

  searchResultNone?: string;

  /**
   * 値を表示値に変換する関数。
   * heading: 各行の手前に、薄い字で表示する部分。必須ではない。
   * main: 表示値。必須。
   */
  toDisplayValue: (value: T) => Promise<SearchDropdownDisplayValue>;

  /**
   * 差分モード時の変更前の値。
   * 差分モードの場合のみ必須。
   */
  changedFrom?: T | null;
  /**
   * 2つの値が同じかどうかを判定する関数。
   * 差分モード時に使用する。
   * 省略した場合は、単純な === で比較する。
   */
  checkEqualityFn?: (v0: T, v1: T) => boolean | Promise<boolean>;

  /**
   * 検索を行う関数。
   * @param text 検索条件となるテキスト。
   * @return 検索結果。
   */
  onSearch?: (text: string) => Promise<T[]>;
  onChange?: (value: T | null) => void;

  /**
   * ポップオーバーを出す時、portalを用いるならtrue。
   * デフォルト値はfalse。
   */
  usePortal?: boolean;
} & ComponentStyleProps;

export const CMFormInputBaseSearchAndSelect = <T extends string | number>({
  editMode = "editable",
  variant = "default",
  size = "md",

  placeholder = "",
  placeholderPopover = "",
  maxLength = 100,
  menuShowMaxCount,

  value = null,

  hideClearButton = false,

  labelSubmit,
  labelClear,

  selectButtonRightIcon = <ChevronDownIcon />,
  popoverTitle,

  searchResultNone,

  toDisplayValue,

  changedFrom,
  checkEqualityFn,

  onSearch,
  onChange,

  usePortal = false,

  sx,
  ...rest
}: CMFormInputBaseSearchAndSelectProps<T>) => {
  const styles = useMultiStyleConfig("CMFormInputBaseSearchAndSelectStyles", {
    size,
  });
  const isDiffMode = useMemo(() => variant === "diff", [variant]);

  const { hasChangedInDiffMode } = useCheckEqualityInDiffMode(
    isDiffMode,
    changedFrom,
    value,
    checkEqualityFn,
  );

  const appliedButtonStyles = useMemo(
    () => ({
      ...styles.buttonBase,
      ...(hasValue(value) ? styles.buttonSelected : styles.buttonUnselected),
      ...(editMode === "disabled" ? styles.buttonDisabled : undefined),
    }),
    [styles, value, editMode],
  );

  const [buttonDisplayValue, setButtonDisplayValue] = useState<string | null>(
    null,
  );
  // 選択値をボタン上の表示値に変換する。
  useEffect(
    () => {
      if (isNullish(value)) {
        setButtonDisplayValue(null);
      } else {
        (async () => {
          setButtonDisplayValue((await toDisplayValue(value)).mainLabel);
        })();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value],
  );

  // ポップアップエリアの表示制御に使用する
  const {
    isOpen: isOpenPopup,
    onClose: onClosePopup,
    onToggle: onTogglePopup,
  } = useDisclosure();

  const {
    isOpen: isOpenMenu,
    onOpen: onOpenMenu,
    onClose: onCloseMenu,
  } = useDisclosure();

  // 入力文字列
  const [inputText, setInputText] = useState<string>("");
  // const debouncedInputText = useDebounce<string>(inputText, 1000);

  // 選択肢一覧の値部分のみ。
  const [optionValues, setOptionValues] = useState<T[]>([]);

  // 選択肢一覧。
  const [options, setOptions] = useState<SearchDropdownOption<T>[]>([]);

  // 確定ボタンが押下されるまで、内部で選択項目を保持する
  const [internalSelectedValue, setInternalSelectedValue] = useState<T | null>(
    value,
  );

  const resetAllInternalState = useCallback(() => {
    setInputText("");
    setOptionValues([]);
    setOptions([]);
    setInternalSelectedValue(null);
    onCloseMenu();
  }, [onCloseMenu]);

  const handleSelectOption = useCallback(
    (value: T) => {
      setInternalSelectedValue(value);

      setInputText("");
      setOptionValues([]);
      setOptions([]);

      onCloseMenu();
    },
    [onCloseMenu],
  );

  const handleChangeInputText = useCallback((value: string) => {
    setInputText(value);
  }, []);
  const handleClearInternal = useCallback(() => {
    resetAllInternalState();
  }, [resetAllInternalState]);

  useDebounce(
    () => {
      log.debug(`debounced onSearch: inputText=${inputText}`);
      if (isNullish(internalSelectedValue) && inputText !== "") {
        (async () => {
          if (isNullish(onSearch)) return;
          const _optionValues = await onSearch(inputText);
          setOptionValues(_optionValues);
          log.debug(
            `debounced onSearch: setOptionValues: optionValues=${JSON.stringify(
              _optionValues,
            )}`,
          );
        })();
      }
    },
    1000,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputText],
  );

  useEffect(
    () => {
      log.debug(
        `CMFormInputSearchAndSelect: useEffect on optionValues=${JSON.stringify(
          optionValues,
        )}`,
      );
      (async () => {
        const _options = await Promise.all(
          optionValues.map(async (v) => ({
            value: v,
            ...(await toDisplayValue(v)),
          })),
        );
        setOptions(_options);
        if (inputText.length > 0 && isNullish(internalSelectedValue)) {
          onOpenMenu();
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [optionValues],
  );

  /**
   * この検索条件全体を確定する。
   */
  const handleSubmit = () => {
    onClosePopup();
    resetAllInternalState();
    onChange?.(internalSelectedValue);
  };

  /**
   * この検索条件全体をクリアする。
   */
  const handleClear = useCallback(() => {
    onClosePopup();
    resetAllInternalState();
    onChange?.(null);
  }, [onChange, onClosePopup, resetAllInternalState]);

  const containerStyles = useMemo(
    () => ({
      ...styles.containerBase,
      ...(hasChangedInDiffMode ? styles.diff : undefined),
      ...sx,
    }),
    [styles, hasChangedInDiffMode, sx],
  );
  const readonlyStyles = useMemo(
    () => ({
      ...styles.readonly,
      ...(hasChangedInDiffMode ? styles.diff : undefined),
      ...sx,
    }),
    [styles, hasChangedInDiffMode, sx],
  );

  const ref = React.useRef(null);
  useOutsideClick({
    ref: ref,
    handler: () => {
      if (!isOpenPopup) return;
      onClosePopup();
      resetAllInternalState();
    },
  });

  const popoverContent = (
    <PopoverContent sx={styles.popoverContent}>
      <PopoverHeader sx={styles.popoverHeader}>
        {hasValue(popoverTitle) && (
          <Text sx={styles.popoverLabel}>{popoverTitle}</Text>
        )}
      </PopoverHeader>
      <PopoverBody>
        <CMFormInputSearchAndSelectPopup<T>
          placeholder={placeholderPopover}
          maxLength={maxLength}
          menuShowMaxCount={menuShowMaxCount}
          labelSubmit={labelSubmit}
          labelClear={labelClear}
          hideClearButton={hideClearButton}
          searchResultNone={searchResultNone}
          toDisplayValue={toDisplayValue}
          inputText={inputText}
          onChangeInputText={handleChangeInputText}
          onClearInputText={handleClearInternal}
          options={options}
          onSelectOption={handleSelectOption}
          internalSelectedValue={internalSelectedValue}
          onSubmit={handleSubmit}
          onClear={handleClear}
          searchResultMenuSettings={{ isOpenMenu, onCloseMenu }}
        />
      </PopoverBody>
    </PopoverContent>
  );

  return editMode === "readOnly" ? (
    <Text sx={readonlyStyles} {...rest}>
      {hasValue(buttonDisplayValue) ? buttonDisplayValue : value}
    </Text>
  ) : editMode === "disabled" ? (
    <Flex direction={"column"} sx={containerStyles} {...rest} ref={ref}>
      <Button
        justifyContent={"space-between"}
        rightIcon={selectButtonRightIcon}
        disabled={true}
        sx={appliedButtonStyles}
      >
        {hasValue(buttonDisplayValue) ? (
          <Text sx={styles.buttonLabel}>{buttonDisplayValue}</Text>
        ) : (
          <Text sx={styles.buttonPlaceholder}>{placeholder}</Text>
        )}
      </Button>
    </Flex>
  ) : (
    <Flex
      direction={"column"}
      sx={containerStyles}
      {...rest}
      ref={ref}
      className={"Flex"}
    >
      <Popover placement="bottom-start" isOpen={isOpenPopup}>
        <PopoverTrigger>
          <Button
            justifyContent={"space-between"}
            rightIcon={selectButtonRightIcon}
            onClick={onTogglePopup}
            sx={appliedButtonStyles}
            className={"Button"}
          >
            {hasValue(buttonDisplayValue) ? (
              <Text sx={styles.buttonLabel}>{buttonDisplayValue}</Text>
            ) : (
              <Text sx={styles.buttonPlaceholder}>{placeholder}</Text>
            )}
          </Button>
        </PopoverTrigger>
        {usePortal ? <Portal>{popoverContent}</Portal> : popoverContent}
      </Popover>
    </Flex>
  );
};

export const CMFormInputBaseSearchAndSelectStyles: ComponentMultiStyleConfig = {
  parts: [
    "containerBase",
    "containerReadOnly",
    "containerReadOnlyShrunk",
    "buttonUnselected",
    "buttonSelected",
    "buttonLabel",
    "buttonPlaceholder",
    "link",
    "readonly",
    "diff",
  ],
  baseStyle: {
    containerBase: {
      justifyContent: "stretch",
      alignItems: "stretch",
    },
    containerReadOnly: {
      _disabled: {
        opacity: 1,
      },
      bgColor: "none",
      borderColor: "none",
    },
    containerReadOnlyShrunk: {},
    buttonUnselected: {
      bgColor: "white",
      color: "gray.400",
      borderWidth: "1px",
      borderColor: "gray.200",
    },
    buttonSelected: {
      bgColor: "white",
      color: "gray.900",
      borderWidth: "1px",
      borderColor: "gray.200",
    },
    buttonDisabled: {
      bgColor: "gray.100",
      color: "gray.400",
      _hover: {
        bgColor: "gray.100",
        color: "gray.400",
      },
    },
    buttonLabel: {
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
      fontWeight: "normal",
    },
    buttonPlaceholder: {
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
      fontWeight: "normal",
      color: "blackAlpha.500",
    },
    popoverContent: {
      w: "400px",
      bg: "gray.50",
    },
    popoverHeader: {
      border: 0,
      pb: 0,
    },
    popoverLabel: {
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
    popoverBody: {
      w: "400px",
      position: "relative",
    },
    buttonGroup: {
      position: "relative",
    },
    confirmButton: {
      variant: "solid",
      height: "25px",
      width: "60px",
      bg: "teal.500",
      color: "white",
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
    clearButton: {
      bg: "gray.200",
      variant: "solid",
      height: "25px",
      width: "60px",
      marginLeft: "100px",
      color: "gray.600",
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
    readonly: {
      maxW: "max-content",
    },
    diff: {
      borderWidth: "1px",
      borderColor: "orange.500",
    },
  },
  sizes: {
    sm: {
      containerBase: {
        h: "22px",
      },
      containerReadOnly: {},
      containerReadOnlyShrunk: {
        h: "18px",
      },
    },
    md: {
      containerBase: {
        h: "30px",
      },
      containerReadOnly: {},
      containerReadOnlyShrunk: {
        h: "24px",
      },
    },
    lg: {
      containerBase: {
        h: "38px",
      },
      containerReadOnly: {},
      containerReadOnlyShrunk: {
        h: "30px",
      },
    },
  },
};
